import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import { getOrgPermissions } from '../../utils/permissions';
import { getCcmStatus, ingestBiasMetric, runCcmChecks } from './aiGovApi';

/** CC-UI — continuous control monitoring dashboard for the three AI checks. */
export default function AiCcmMonitoring() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIngest, setShowIngest] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getCcmStatus()); } catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runChecks = async () => {
    setRunning(true);
    try { setData(await runCcmChecks()); } catch { /* keep current */ } finally { setRunning(false); }
  };

  const checks = data?.checks || [];

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.ccmTitle', 'Continuous Monitoring')}</h1>
        <p className="aig-subtitle">{t('AIGovernance.ccmSubtitle', 'Scheduled data-lineage, algorithmic-bias, model access-control and prompt-egress checks over your AI assets.')}</p>
      </div>

      <AiGovNav />

      <div className="aig-toolbar">
        <div className="aig-spacer" />
        {canManageFrameworks && (
          <>
            <button className="aig-btn" disabled={running} onClick={runChecks}>
              {running ? t('AIGovernance.runningChecks', 'Running checks…') : t('AIGovernance.runChecks', 'Run checks now')}
            </button>
            <button className="aig-btn aig-btn-primary" onClick={() => setShowIngest(true)}>+ {t('AIGovernance.ingestBias', 'Ingest fairness metric')}</button>
          </>
        )}
      </div>

      {loading ? (
        <div className="aig-spinner" />
      ) : !data ? (
        <div className="aig-empty">{t('AIGovernance.ccmUnavailable', 'No continuous-monitoring data yet.')}</div>
      ) : (
        <>
          <div className="aig-grid">
            {checks.map((c) => (
              <div className="aig-card" key={c.key}>
                <h3 className="aig-card-title">{c.label}</h3>
                <div className="aig-card-row">
                  <span>{t('AIGovernance.status', 'Status')}</span>
                  <span className={`aig-badge ${c.status === 'PASS' ? 'aig-b-pass' : c.status === 'NOT_RUN' ? 'aig-b-na' : 'aig-b-fail'}`}>{c.status}</span>
                </div>
                <div className="aig-card-row"><span>{t('AIGovernance.openFindings', 'Open findings')}</span><strong className={c.openFindings ? 'aig-val-bad' : 'aig-val-ok'}>{c.openFindings}</strong></div>
                {Object.entries(c.bySeverity || {}).map(([sev, n]) => (
                  <div className="aig-card-row" key={sev}><span className="aig-muted">{sev}</span><span>{n}</span></div>
                ))}
                {c.key === 'dataEgress' && (
                  <>
                    <div className="aig-card-row">
                      <span>{t('AIGovernance.egressEvents90d', 'Egress events (90d)')}</span>
                      <strong className={data.egressEventCount ? 'aig-val-ok' : 'aig-val-bad'}>{data.egressEventCount ?? 0}</strong>
                    </div>
                    {!data.egressEventCount && (
                      <p className="aig-muted" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                        {t('AIGovernance.egressHint', 'No prompt-egress evidence yet. Deploy')}{' '}
                        <a href="https://github.com/cataam-security/cataam/tree/main/prompt-guard" target="_blank" rel="noreferrer">Cataam Prompt Guard</a>{' '}
                        {t('AIGovernance.egressHint2', '(open source) on browsers/endpoints and push its evidence to log what leaves for public LLMs (EU AI Act Art.12).')}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <p className="aig-muted" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
            {t('AIGovernance.biasMetricCount', 'Fairness metrics recorded')}: {data.biasMetricCount}.{' '}
            {t('AIGovernance.egressEventCount', 'Prompt-egress events recorded')}: {data.egressEventCount ?? 0}.{' '}
            {t('AIGovernance.ccmNote', 'Checks run automatically on each connector sync and on the scheduled audit; findings latch into your ISO 42001 / NIST AI RMF audit.')}
          </p>
        </>
      )}

      {showIngest && <IngestModal t={t} onClose={() => setShowIngest(false)} onSaved={() => { setShowIngest(false); load(); }} />}
    </div>
  );
}

function IngestModal({ t, onClose, onSaved }) {
  const [form, setForm] = useState({
    assetId: '', metricType: 'DISPARATE_IMPACT', protectedAttribute: '', metricValue: '', threshold: '', passed: 'true', sampleSize: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      await ingestBiasMetric({
        assetId: form.assetId ? Number(form.assetId) : null,
        metricType: form.metricType,
        protectedAttribute: form.protectedAttribute,
        metricValue: form.metricValue ? Number(form.metricValue) : null,
        threshold: form.threshold ? Number(form.threshold) : null,
        passed: form.passed === 'true',
        sampleSize: form.sampleSize ? Number(form.sampleSize) : null,
      });
      onSaved();
    } catch { setSaving(false); }
  };
  return (
    <div className="aig-overlay" onClick={onClose}>
      <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('AIGovernance.ingestBias', 'Ingest fairness metric')}</h3>
        <div className="aig-field"><label>{t('AIGovernance.assetId', 'AI asset id')}</label>
          <input value={form.assetId} onChange={(e) => set('assetId', e.target.value)} placeholder="e.g. 1234" /></div>
        <div className="aig-field"><label>{t('AIGovernance.metricType', 'Metric type')}</label>
          <select value={form.metricType} onChange={(e) => set('metricType', e.target.value)}>
            {['DISPARATE_IMPACT', 'DEMOGRAPHIC_PARITY', 'EQUAL_OPPORTUNITY', 'ACCURACY_GAP'].map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div className="aig-field"><label>{t('AIGovernance.protectedAttribute', 'Protected attribute')}</label>
          <input value={form.protectedAttribute} onChange={(e) => set('protectedAttribute', e.target.value)} placeholder="gender / race / age_group" /></div>
        <div className="aig-field"><label>{t('AIGovernance.metricValue', 'Measured value')}</label>
          <input type="number" step="0.0001" value={form.metricValue} onChange={(e) => set('metricValue', e.target.value)} /></div>
        <div className="aig-field"><label>{t('AIGovernance.threshold', 'Threshold')}</label>
          <input type="number" step="0.0001" value={form.threshold} onChange={(e) => set('threshold', e.target.value)} /></div>
        <div className="aig-field"><label>{t('AIGovernance.passed', 'Within threshold?')}</label>
          <select value={form.passed} onChange={(e) => set('passed', e.target.value)}><option value="true">{t('AIGovernance.yes', 'Yes')}</option><option value="false">{t('AIGovernance.no', 'No')}</option></select></div>
        <div className="aig-field"><label>{t('AIGovernance.sampleSize', 'Sample size')}</label>
          <input type="number" value={form.sampleSize} onChange={(e) => set('sampleSize', e.target.value)} /></div>
        <div className="aig-modal-actions">
          <button className="aig-btn" onClick={onClose}>{t('AIGovernance.cancel', 'Cancel')}</button>
          <button className="aig-btn aig-btn-primary" disabled={saving} onClick={submit}>{t('AIGovernance.save', 'Save')}</button>
        </div>
      </div>
    </div>
  );
}
